import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import {
  Avatar,
  Box,
  Container,
  Divider,
  Grid,
  LinearProgress,
  ListItemText,
  Typography,
  Button,
  Menu,
  ListItemIcon,
  MenuItem,
} from '@material-ui/core'
import Error from 'components/error'
import { PageHeader } from 'components/page-header'
import Footer from 'components/footer'
import { roles, withProtectedRoute } from 'lib/auth'
import { GET_STUDENTS_AND_INVITED_GUARDIANS } from './queries'
import MoreHoriz from 'components/material-symbol/icons/MoreHoriz'
import PersonOutline from 'components/material-symbol/icons/PersonOutline'
import AssignmentIndOutlinedIcon from 'components/material-symbol/icons/AssignmentInd'
import { useStyles, StyledChip } from './styles'
import EditOutlined from 'components/material-symbol/icons/Edit'
import { formatPhoneNumber } from 'utils/phoneNumber'
import hasNonNullProperty from 'lib/utils/has-non-null-property'
import InviteGuardianModal from './components/invite-guardian-modal'
import InviteGuardianItem from './components/invited-guardian-item'

const StudentItem = ({ student, relationshipType }) => {
  const history = useHistory()
  const classes = useStyles()
  const [anchorEl, setAnchorEl] = useState(null)
  const hasCompletedProfile = hasNonNullProperty(student?.golferProfile)

  const handleMoreClick = event => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }
  const handleClose = event => {
    event.stopPropagation()
    setAnchorEl(null)
  }

  const handleViewProfileClick = event => {
    handleClose(event)
    history.push(`/account/students/${student.id}/profile/edit`)
  }

  const handleEditDetailsClick = event => {
    handleClose(event)
    history.push(`/account/students/${student.id}/edit`)
  }

  return (
    <Grid
      container
      item
      spacing={1}
      className={classes.studentItem}
      onClick={() => history.push(`/account/students/${student.id}`)}
    >
      <Grid lg={3} xs={5} item>
        <AvatarWithNameAndAgeGroup
          student={student}
          relationshipType={relationshipType}
        />
      </Grid>
      <Grid lg={4} className={classes.hideOnMobile} item>
        <StudentContact student={student} field="email" />
      </Grid>
      <Grid lg={2} className={classes.hideOnMobile} item>
        <StudentContact student={student} field="phone" />
      </Grid>
      <Grid lg={2} xs={5} item className={classes.center}>
        <ProfileButton hasCompletedProfile={hasCompletedProfile} />
      </Grid>
      <Grid lg={1} xs={2} item>
        <Button
          aria-controls={`simple-menu-${student.id}`}
          aria-haspopup="true"
          data-testid="menu-button"
          onClick={handleMoreClick}
        >
          <MoreHoriz fontSize="small" />
        </Button>
        <Menu
          id={`simple-menu-${student.id}`}
          keepMounted
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          getContentAnchorEl={null}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <MenuItem
            onClick={() => history.push(`/account/students/${student.id}`)}
          >
            <ListItemIcon>
              <PersonOutline className={classes.icon} />
            </ListItemIcon>
            <ListItemText primary="View Account Details" />
          </MenuItem>
          <MenuItem onClick={handleEditDetailsClick}>
            <ListItemIcon>
              <EditOutlined className={classes.icon} />
            </ListItemIcon>
            <ListItemText primary="Edit Account Details" />
          </MenuItem>
          <MenuItem
            onClick={handleViewProfileClick}
            data-testid="profile-button"
          >
            <ListItemIcon>
              <AssignmentIndOutlinedIcon className={classes.icon} />
            </ListItemIcon>
            <ListItemText
              primary={
                hasCompletedProfile
                  ? 'Edit Golfer Profile'
                  : 'Complete Golfer Profile'
              }
            />
          </MenuItem>
        </Menu>
      </Grid>
    </Grid>
  )
}

const StudentsPage = () => {
  const { data, loading, error, refetch } = useQuery(
    GET_STUDENTS_AND_INVITED_GUARDIANS,
  )
  const classes = useStyles()
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)

  if (loading) return <LinearProgress />
  if (error) return <Error error={error} />

  const representatives = data?.currentUser?.representatives
  const minorStudents = representatives
    ?.map(rep => rep.student)
    .filter(student => student.isMinor)

  const invitedGuardians = data?.jrlGuardianInvitations || []
  const canAddGuardian = data?.currentUser?.attributes?.jrlPlayers?.length > 0

  const crumbs = [
    {
      label: 'Home',
      to: '',
    },
  ]

  return (
    <>
      <PageHeader
        title="Accounts"
        crumbs={crumbs}
        actions={
          canAddGuardian ? (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setIsInviteModalOpen(true)}
            >
              Add Guardian
            </Button>
          ) : null
        }
      />
      <Box className={classes.accountsWrapper}>
        <Container maxWidth="lg" style={{ paddingBottom: '1rem' }}>
          <Typography
            className={classes.overlineText}
            variant="overline"
            component="div"
          >
            Accounts ({representatives.length})
          </Typography>
          <Divider style={{ width: '100%', marginBottom: '1rem' }} />
          {representatives.map(rep => (
            <StudentItem
              key={rep.student.id}
              student={rep.student}
              relationshipType={rep.relationshipType}
            />
          ))}
          {invitedGuardians.length > 0 && (
            <>
              <Typography
                className={classes.overlineText}
                style={{ marginTop: '2rem' }}
                variant="overline"
                component="div"
              >
                Invited Guardians ({invitedGuardians.length})
              </Typography>
              <Divider style={{ width: '100%', marginBottom: '1rem' }} />
            </>
          )}
          {invitedGuardians.map(ig => (
            <InviteGuardianItem
              key={ig.id}
              invitedGuardian={ig}
              refetch={refetch}
            />
          ))}
        </Container>
        <Footer />
      </Box>

      <InviteGuardianModal
        open={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        students={minorStudents}
        refetch={refetch}
      />
    </>
  )
}

function AvatarWithNameAndAgeGroup({ student, relationshipType }) {
  const name = `${student?.firstName} ${student?.lastName}`
  let subtitle = student?.isMinor ? 'Junior' : 'Adult'
  if (relationshipType === 'SELF') {
    subtitle = 'Account Owner'
  }
  return (
    <Grid item>
      <Grid container direction="row" alignItems="center" spacing={2}>
        <Grid item>
          <Avatar alt={name} />
        </Grid>
        <Grid item>
          <ListItemText primary={name} secondary={subtitle} />
        </Grid>
      </Grid>
    </Grid>
  )
}

function StudentContact({ student, field }) {
  let value = ''
  if (field === 'phone') {
    value = formatPhoneNumber(student?.contact[field])
  } else {
    value = student?.contact[field]
  }
  return <ListItemText primary={value} style={{ overflowWrap: 'anywhere' }} />
}

function ProfileButton({ hasCompletedProfile }) {
  return hasCompletedProfile ? (
    <StyledChip label="Completed Profile" />
  ) : (
    <StyledChip label="Incomplete Profile" bgColor="#9e9e9e" />
  )
}

export default withProtectedRoute(StudentsPage, [roles.CONSUMER])
